<template>
  <div class="flex w-full flex-col gap-6">
    <header class="flex flex-col gap-3 border-b pb-6">
      <div class="flex items-center gap-3 text-sm font-semibold uppercase tracking-[0.2em] text-cyan-700 dark:text-cyan-300">
        <UsersRound class="h-5 w-5" />
        Holiday administration
      </div>
      <h1 class="text-3xl font-semibold tracking-tight">
        Company users
      </h1>
      <p class="max-w-3xl text-sm leading-6 text-muted-foreground">
        Choose a company, then assign or remove the users who belong to it.
      </p>
    </header>

    <Alert v-if="store.error" variant="destructive">
      <CircleAlert class="h-4 w-4" />
      <AlertTitle>Company relationships unavailable</AlertTitle>
      <AlertDescription>{{ store.error }}</AlertDescription>
    </Alert>

    <div class="grid gap-6 xl:grid-cols-[minmax(18rem,0.8fr)_minmax(0,1.4fr)]">
      <section class="flex min-w-0 flex-col gap-4 rounded-xl border bg-card p-4">
        <div class="flex flex-col gap-1">
          <h2 class="font-semibold">
            Companies
          </h2>
          <p class="text-sm text-muted-foreground">
            {{ store.companyPagination.total }} available
          </p>
        </div>

        <form class="flex gap-2" @submit.prevent="searchCompanies">
          <Input v-model="store.companySearch" aria-label="Search companies" placeholder="Name or account number" />
          <Button type="submit" variant="secondary" size="icon" :loading="store.isLoadingCompanies" aria-label="Search companies">
            <Search class="h-4 w-4" />
          </Button>
        </form>

        <div v-if="store.isLoadingCompanies && store.companies.length === 0" class="flex min-h-40 items-center justify-center gap-2 text-sm text-muted-foreground">
          <LoaderCircle class="h-4 w-4 animate-spin" />
          Loading companies…
        </div>

        <div v-else class="flex flex-col gap-2">
          <button v-for="company in store.companies" :key="company.id" type="button" :class="companyButtonClass(company)" @click="selectCompany(company)">
            <span class="min-w-0 flex-1 text-left">
              <span class="block truncate font-medium">{{ company.name }}</span>
              <span class="block truncate text-xs text-muted-foreground">{{ company.startel_account_number || 'No account number' }}</span>
            </span>
            <span class="rounded-full bg-muted px-2 py-0.5 text-xs text-muted-foreground">{{ company.users_count }}</span>
          </button>

          <p v-if="!store.isLoadingCompanies && store.companies.length === 0" class="py-10 text-center text-sm text-muted-foreground">
            No companies found.
          </p>
        </div>

        <div v-if="store.companyPagination.lastPage > 1" class="flex items-center justify-between gap-2 border-t pt-4">
          <Button type="button" variant="outline" size="sm" :disabled="store.isLoadingCompanies || store.companyPagination.currentPage === 1" @click="loadCompanies(store.companyPagination.currentPage - 1)">
            Previous
          </Button>
          <span class="text-xs text-muted-foreground">{{ store.companyPagination.currentPage }} / {{ store.companyPagination.lastPage }}</span>
          <Button type="button" variant="outline" size="sm" :disabled="store.isLoadingCompanies || store.companyPagination.currentPage === store.companyPagination.lastPage" @click="loadCompanies(store.companyPagination.currentPage + 1)">
            Next
          </Button>
        </div>
      </section>

      <section class="min-w-0 rounded-xl border bg-card p-5">
        <template v-if="store.selectedCompany">
          <div class="flex flex-col gap-1 border-b pb-4">
            <h2 class="text-xl font-semibold">
              {{ store.selectedCompany.name }}
            </h2>
            <p class="text-sm text-muted-foreground">
              {{ companyDetails }}
            </p>
          </div>

          <form class="grid gap-3 border-b py-5 md:grid-cols-[minmax(0,1fr)_auto] md:items-end" @submit.prevent="assignSelectedUser">
            <UserSelect v-model="selectedUser" label="Assign user" placeholder="Search users by name or email" :disabled="store.isAssigning" />
            <Button type="submit" :disabled="store.isAssigning || !selectedUser">
              <UserPlus class="h-4 w-4" />
              Assign user
            </Button>
          </form>

          <div class="flex flex-col gap-4 pt-5">
            <div class="flex items-center justify-between gap-3">
              <h3 class="font-semibold">
                Assigned users
              </h3>
              <span class="text-sm text-muted-foreground">{{ store.userPagination.total }} users</span>
            </div>

            <div class="overflow-hidden rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>User</TableHead>
                    <TableHead class="text-right">
                      Relationship
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  <TableRow v-for="user in store.users" :key="user.id">
                    <TableCell>
                      <div class="flex min-w-0 flex-col gap-1">
                        <span class="font-medium">{{ user.name }}</span>
                        <span class="text-xs text-muted-foreground">{{ user.email }}</span>
                      </div>
                    </TableCell>
                    <TableCell class="text-right">
                      <Button type="button" variant="destructive" size="sm" :disabled="store.removingUserIds.includes(user.id)" @click="removeUser(user)">
                        <UserMinus class="h-4 w-4" />
                        Remove
                      </Button>
                    </TableCell>
                  </TableRow>
                  <TableRow v-if="!store.isLoadingUsers && store.users.length === 0">
                    <TableCell colspan="2" class="h-24 text-center text-sm text-muted-foreground">
                      No users are assigned to this company.
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </div>

            <div v-if="store.isLoadingUsers" class="flex items-center justify-center gap-2 text-sm text-muted-foreground">
              <LoaderCircle class="h-4 w-4 animate-spin" />
              Loading assigned users…
            </div>

            <div v-if="store.userPagination.lastPage > 1" class="flex items-center justify-end gap-2">
              <Button type="button" variant="outline" size="sm" :disabled="store.isLoadingUsers || store.userPagination.currentPage === 1" @click="loadUsers(store.userPagination.currentPage - 1)">
                Previous
              </Button>
              <span class="text-xs text-muted-foreground">{{ store.userPagination.currentPage }} / {{ store.userPagination.lastPage }}</span>
              <Button type="button" variant="outline" size="sm" :disabled="store.isLoadingUsers || store.userPagination.currentPage === store.userPagination.lastPage" @click="loadUsers(store.userPagination.currentPage + 1)">
                Next
              </Button>
            </div>
          </div>
        </template>

        <div v-else class="flex min-h-80 flex-col items-center justify-center gap-2 text-center text-muted-foreground">
          <Building2 class="h-9 w-9" />
          <p class="font-medium text-foreground">
            Choose a company
          </p>
          <p class="text-sm">
            Select a company from the list to manage its users.
          </p>
        </div>
      </section>
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted, ref } from 'vue'
import { toast } from 'vue-sonner'
import { Building2, CircleAlert, LoaderCircle, Search, UserMinus, UserPlus, UsersRound } from 'lucide-vue-next'
import UserSelect from '@User-manager/components/users/UserSelect.vue'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { useCompanyUserStore } from '../stores/companyUserStore.js'

const store = useCompanyUserStore()
const selectedUser = ref(null)
const companyDetails = computed(() => store.selectedCompany.startel_account_number || 'No account number')

const companyButtonClass = company => [
  'flex w-full items-center gap-3 rounded-lg border px-3 py-3 transition-colors',
  store.selectedCompany?.id === company.id ? 'border-cyan-500 bg-cyan-50 dark:bg-cyan-950/30' : 'hover:bg-muted/60',
]

const loadCompanies = async (page, selectFirst = false) => {
  try {
    const companies = await store.fetchCompanies(page)

    if (selectFirst && companies.length > 0) {
      await store.selectCompany(companies[0])
    }
  } catch {
  }
}

const searchCompanies = () => loadCompanies(1, true)

const selectCompany = async company => {
  selectedUser.value = null

  try {
    await store.selectCompany(company)
  } catch {
  }
}

const loadUsers = async page => {
  try {
    await store.fetchUsers(page)
  } catch {
  }
}

const assignSelectedUser = async () => {
  if (!selectedUser.value) {
    return
  }

  try {
    await store.assignUser(selectedUser.value)
    selectedUser.value = null
    toast.success('User assigned to company.')
  } catch {
    toast.error(store.error)
  }
}

const removeUser = async user => {
  if (!window.confirm(`Remove ${user.name} from ${store.selectedCompany.name}?`)) {
    return
  }

  try {
    await store.removeUser(user)
    toast.success('User removed from company.')
  } catch {
    toast.error(store.error)
  }
}

onMounted(() => loadCompanies(1, true))
</script>
